import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import {
    X,
    Loader2,
    DollarSign,
    Calendar as CalendarIcon,
    User,
    CheckCircle,
    Copy,
    ExternalLink
} from 'lucide-react';

interface Athlete {
    id: string;
    full_name: string;
    email: string;
}

interface CreateChargeModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function CreateChargeModal({ isOpen, onClose }: CreateChargeModalProps) {
    const { user, tenant } = useAuth();
    const queryClient = useQueryClient();
    const [selectedAthleteId, setSelectedAthleteId] = useState('');
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState('');
    const [successData, setSuccessData] = useState<any>(null);

    // Fetch active athletes for selection
    const { data: athletes, isLoading: loadingAthletes } = useQuery({
        queryKey: ['active-athletes', tenant?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('student_pairings')
                .select(`
                    student:student_id (id, full_name, email)
                `)
                .eq('tenant_id', tenant?.id)
                .eq('status', 'active');

            if (error) throw error;
            return (data || []).map(d => d.student) as unknown as Athlete[];
        },
        enabled: isOpen && !!tenant?.id,
    });

    const createChargeMutation = useMutation({
        mutationFn: async () => {
            // 1. Create Invoice in Supabase
            const { data: invoice, error: invoiceError } = await supabase
                .from('invoices')
                .insert([{
                    user_id: selectedAthleteId,
                    tenant_id: tenant?.id,
                    subscription_id: '00000000-0000-0000-0000-000000000000', // Placeholder for standalone charge
                    amount_cents: Math.round(parseFloat(amount) * 100),
                    total_cents: Math.round(parseFloat(amount) * 100),
                    due_date: dueDate,
                    description: description || 'Treino Avulso',
                    status: 'draft'
                }])
                .select()
                .single();

            if (invoiceError) throw invoiceError;

            // 2. Call Edge Function to create charge in Asaas
            const { data: edgeData, error: edgeError } = await supabase.functions.invoke('create-charge', {
                body: { invoice_id: invoice.id }
            });

            if (edgeError) throw edgeError;
            return edgeData;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            setSuccessData(data);
        },
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-primary-600" />
                        Nova Cobrança
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    {successData ? (
                        <div className="text-center space-y-6 animate-in zoom-in-95 duration-300">
                            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle className="w-10 h-10 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Cobrança Gerada!</h3>
                                <p className="text-sm text-gray-500 mt-1">O atleta receberá o link por email.</p>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4 space-y-3">
                                <button
                                    onClick={() => window.open(successData.hosted_invoice_url, '_blank')}
                                    className="w-full py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Ver Fatura
                                </button>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(successData.hosted_invoice_url);
                                        alert('Link copiado!');
                                    }}
                                    className="w-full py-3 bg-primary-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
                                >
                                    <Copy className="w-4 h-4" />
                                    Copiar Link de Pagamento
                                </button>
                            </div>

                            <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-700">
                                Fechar Janela
                            </button>
                        </div>
                    ) : (
                        <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); createChargeMutation.mutate(); }}>
                            {/* Athlete Selection */}
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                    <User className="w-4 h-4" /> Selecionar Atleta
                                </label>
                                <select
                                    value={selectedAthleteId}
                                    onChange={(e) => setSelectedAthleteId(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 transition-all"
                                    required
                                >
                                    <option value="">Escolha um atleta...</option>
                                    {athletes?.map((a: any) => (
                                        <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {/* Amount */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        <DollarSign className="w-4 h-4" /> Valor (R$)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        placeholder="0,00"
                                        className="w-full bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 transition-all font-mono"
                                        required
                                    />
                                </div>

                                {/* Due Date */}
                                <div>
                                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        <CalendarIcon className="w-4 h-4" /> Vencimento
                                    </label>
                                    <input
                                        type="date"
                                        value={dueDate}
                                        onChange={(e) => setDueDate(e.target.value)}
                                        className="w-full bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 transition-all"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 block">
                                    Descrição (Opcional)
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Ex: Consultoria Mensal, Treino de Verão..."
                                    className="w-full bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 transition-all min-h-[80px]"
                                />
                            </div>

                            {createChargeMutation.error && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-xl border border-red-100 dark:border-red-900/30">
                                    Erro: {(createChargeMutation.error as any).message}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={createChargeMutation.isPending || !selectedAthleteId || !amount}
                                className="w-full py-4 bg-primary-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary-600/20 hover:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
                            >
                                {createChargeMutation.isPending ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Gerando Cobrança...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-5 h-5" />
                                        Criar e Enviar Cobrança
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
